import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Page, PageHeader, inputCls, EmptyState } from "@/components/lims";
import { ShieldCheck, Plus, PencilSimple, Trash, SignOut, CheckCircle } from "@phosphor-icons/react";

const ACTION_STYLE = {
  create: "bg-status-ok/10 text-status-ok border-status-ok/30",
  update: "bg-blue-50 text-blue-700 border-blue-200",
  delete: "bg-status-critical/10 text-status-critical border-status-critical/30",
  release: "bg-status-info/10 text-status-info border-status-info/30",
};

const ENTITY_TYPES = ["Sample", "Test", "Order", "Client", "Assay"];
const ACTIONS = ["create", "update", "delete", "release"];

export default function AuditTrail() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (entity) params.set("entity_type", entity);
    if (action) params.set("action", action);
    const { data } = await api.get(`/audit?${params.toString()}`);
    setEntries(data);
    setLoading(false);
  }, [entity, action]);
  useEffect(() => { load(); }, [load]);

  const fmt = (ts) => {
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  return (
    <Page>
      <PageHeader title="Audit Trail" subtitle="Immutable, append-only record of every action (ALCOA+)" />

      <div className="flex flex-wrap gap-3 mb-4">
        <select aria-label="Filter audit events by entity" data-testid="audit-entity-filter" value={entity} onChange={(e) => setEntity(e.target.value)} className={`${inputCls} max-w-[180px]`}>
          <option value="">All entities</option>
          {ENTITY_TYPES.map((e) => <option key={e}>{e}</option>)}
        </select>
        <select aria-label="Filter audit events by action" data-testid="audit-action-filter" value={action} onChange={(e) => setAction(e.target.value)} className={`${inputCls} max-w-[180px]`}>
          <option value="">All actions</option>
          {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">{[...Array(6)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
        ) : entries.length === 0 ? (
          <EmptyState icon={ShieldCheck} title="No audit records" subtitle="Actions across the LIMS will appear here" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Timestamp</th>
                <th className="px-5 py-3 font-semibold">Actor</th>
                <th className="px-5 py-3 font-semibold">Action</th>
                <th className="px-5 py-3 font-semibold">Entity</th>
                <th className="px-5 py-3 font-semibold">Detail</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} data-testid="audit-row" className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">{fmt(e.timestamp)}</td>
                  <td className="px-5 py-3 text-slate-700">{e.actor}<div className="text-[10px] uppercase tracking-wider text-slate-400">{e.actor_role}</div></td>
                  <td className="px-5 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border capitalize ${ACTION_STYLE[e.action] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{e.action}</span>
                  </td>
                  <td className="px-5 py-3 text-slate-600">{e.entity_type} <span className="font-mono text-xs text-slate-400">{e.entity_code}</span></td>
                  <td className="px-5 py-3 text-slate-600">{e.summary}{e.reason && <div className="text-xs text-slate-400 italic mt-0.5">Reason: {e.reason}</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Page>
  );
}
